import { useCallback, useState } from '@wordpress/element';
import { AREA_TO_SECTION_ID } from '../constants/areaMappings';
import { FIELD_CONFIGS } from '../constants/fieldTypes';

export const useCardSections = (initialSections) => {
  const [sections, setSections] = useState(initialSections);

  const addField = useCallback((area, fieldType) => {
    const sectionId = AREA_TO_SECTION_ID[area];
    const fieldConfig = FIELD_CONFIGS.find(f => f.id === fieldType);
    
    if (!fieldConfig) return;

    // Validation for thumbnail area - only one image type allowed
    if (area === 'thumbnail') {
      const thumbnailSection = sections.find(s => s.id === sectionId);
      const hasImageType = thumbnailSection?.fields.some(field => 
        field.type === 'image' || field.type === 'carousel'
      );
      
      // Prevent adding image/carousel if one already exists
      if ((fieldType === 'image' || fieldType === 'carousel') && hasImageType) {
        //console.warn('Thumbnail area can only have one image type (Thumbnail OR Carousel)');
        return; // Prevent the addition
      }
    }

    const newField = {
      id: `${fieldType}-${Date.now()}`,
      type: fieldType,
      label: fieldConfig.title,
      mode: fieldConfig.mode || 'both',
      value: fieldConfig.defaultValue || {},
      order: sections.find(s => s.id === sectionId)?.fields.length || 0
    };

    setSections(prev => prev.map(section => 
      section.id === sectionId 
        ? { 
            ...section, 
            fields: [...section.fields, newField],
            order: section.order || 0
          } 
        : section
    ));
  }, [sections]);

  const updateField = useCallback((fieldId, updates) => {
    setSections(prev => prev.map(section => ({
      ...section,
      fields: section.fields.map(field => 
        field.id === fieldId ? { 
          ...field, 
          ...updates,
          mode: updates.mode !== undefined ? updates.mode : field.mode
        } : field
      )
    })));
  }, []);

  const deleteField = useCallback((fieldId) => {
    setSections(prev => prev.map(section => ({
      ...section,
      fields: section.fields.filter(field => field.id !== fieldId)
    })));
  }, []);

  // Move field up in order
  const moveFieldUp = useCallback((fieldId) => {
    setSections(prev => prev.map(section => {
      const fieldIndex = section.fields.findIndex(field => field.id === fieldId);
      if (fieldIndex <= 0) return section; // Can't move first item up
      
      const newFields = [...section.fields];
      [newFields[fieldIndex - 1], newFields[fieldIndex]] = [newFields[fieldIndex], newFields[fieldIndex - 1]];
      
      // Update order for moved fields
      newFields.forEach((field, index) => {
        field.order = index;
      });
      
      return { ...section, fields: newFields };
    }));
  }, []);

  // Move field down in order
  const moveFieldDown = useCallback((fieldId) => {
    setSections(prev => prev.map(section => {
      const fieldIndex = section.fields.findIndex(field => field.id === fieldId);
      if (fieldIndex === -1 || fieldIndex >= section.fields.length - 1) return section; // Can't move last item down
      
      const newFields = [...section.fields];
      [newFields[fieldIndex], newFields[fieldIndex + 1]] = [newFields[fieldIndex + 1], newFields[fieldIndex]];
      
      // Update order for moved fields
      newFields.forEach((field, index) => {
        field.order = index;
      });
      
      return { ...section, fields: newFields };
    }));
  }, []);

  const getSectionByArea = useCallback((area) => {
    const sectionId = AREA_TO_SECTION_ID[area];
    return sections.find(section => section.id === sectionId);
  }, [sections]);

  // Set sections from external source (API)
  const setSectionsFromAPI = useCallback((apiSections) => {
    if (apiSections && Array.isArray(apiSections)) {
      // Ensure all sections have proper order and fields
      const processedSections = apiSections.map((section, index) => ({
        ...section,
        mode: section.mode || 'both',
        order: section.order || index,
        fields: section.fields?.map((field, fieldIndex) => ({
          ...field,
          mode: field.mode || 'both',
          order: field.order || fieldIndex
        })) || []
      }));
      setSections(processedSections);
    }
  }, []);

  return { 
    sections, 
    addField, 
    updateField, 
    deleteField,
    moveFieldUp,
    moveFieldDown,
    getSectionByArea,
    setSections: setSectionsFromAPI
  };
};